import { useQuery } from '@tanstack/react-query';
import type { CSSProperties, ReactNode, Ref } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usersSearchQueryOptions } from '../../api/users';
import { type User } from '../../types';
import { UserResultRow } from './UserResultRow';

/**
 * Entra user autocomplete shared by the topbar search and the admin
 * "add user" form. The parent owns the input text (`value`); the combobox
 * owns the debounce, the dropdown and the keyboard navigation, and reports
 * picks through `onSelect`.
 */
export function UserSearchCombobox({
  value,
  onValueChange,
  onSelect,
  placeholder,
  className,
  inputClassName,
  inputStyle,
  inputRef,
  autoFocus,
  clearOnEscape = false,
  getItemHref,
  onOpenInNewTab,
  renderTrailing,
  renderEmptyQuery,
}: {
  value: string;
  onValueChange: (value: string) => void;
  onSelect: (user: User) => void;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  inputStyle?: CSSProperties;
  inputRef?: Ref<HTMLInputElement>;
  autoFocus?: boolean;
  clearOnEscape?: boolean;
  /**
   * When the pick leads to a page, return its URL: each result then gets an
   * "open in a new tab" link next to it. Omit it where picking a result only
   * fills a field (e.g. the admin "add user" form) — there is nothing to open.
   */
  getItemHref?: (user: User) => string;
  /**
   * Fired when a result is opened in a new tab, by its cell or by
   * Cmd/Ctrl+Enter. Visiting someone counts whichever tab it happens in, so
   * the topbar files them under its recents here.
   */
  onOpenInNewTab?: (user: User) => void;
  /** Rendered inside the input wrapper (e.g. the topbar "/" badge). */
  renderTrailing?: (loading: boolean) => ReactNode;
  /** Rendered in the dropdown when open with an empty query (e.g. recents). */
  renderEmptyQuery?: (close: () => void) => ReactNode;
}) {
  const { t } = useTranslation();
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  // Set when a pick fills the input, so the debounce below doesn't
  // immediately reopen the dropdown on that programmatic value change.
  const skipNextOpenRef = useRef(false);

  // The fetch is gated by `enabled: q.length >= 2` inside
  // usersSearchQueryOptions (no API call for a 1-char term); it also gives us
  // 30s caching and request cancellation. The debounce below just feeds its key.
  const { data: results = [], isFetching: loading } = useQuery(
    usersSearchQueryOptions(debouncedQuery),
  );

  // The 250ms debounce lives here. The `length >= 2` check is NOT the request
  // gate (that's `enabled` above) — it only opens the dropdown, so a single
  // character doesn't pop open an empty menu.
  useEffect(() => {
    const id = setTimeout(() => {
      const trimmed = value.trim();
      setDebouncedQuery(trimmed);
      setCursor(0);
      if (skipNextOpenRef.current) {
        skipNextOpenRef.current = false;
        return;
      }
      if (trimmed.length >= 2) {
        setOpen(true);
      }
    }, 250);
    return () => clearTimeout(id);
  }, [value]);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (
        wrapperRef.current &&
        e.target instanceof Node &&
        !wrapperRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  function pick(user: User) {
    skipNextOpenRef.current = true;
    setOpen(false);
    onSelect(user);
  }

  // Opening in a new tab leaves this tab where it was, so the dropdown stays
  // up on purpose: it lets you fan several people out into tabs without
  // retyping the search between each one.
  function noteOpenedInNewTab(user: User) {
    onOpenInNewTab?.(user);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    // Enter also confirms an IME candidate (Chinese, Japanese, Korean input):
    // that keystroke belongs to the composition, not to this list.
    if (e.nativeEvent.isComposing) {
      return;
    }
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      if (results.length > 0) {
        setOpen(true);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => (results.length === 0 ? 0 : (c + 1) % results.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) =>
        results.length === 0 ? 0 : (c - 1 + results.length) % results.length,
      );
    } else if (e.key === 'Enter') {
      // Only swallow Enter when the dropdown has a highlighted result;
      // otherwise let it bubble (e.g. to submit an enclosing form).
      const highlighted = open ? results[cursor] : undefined;
      if (!highlighted) {
        return;
      }
      e.preventDefault();
      // Cmd/Ctrl+Enter mirrors the row's open-in-new-tab cell, the same way
      // the modifier works on a link anywhere else.
      const href =
        e.metaKey || e.ctrlKey ? getItemHref?.(highlighted) : undefined;
      if (href !== undefined) {
        window.open(href, '_blank', 'noreferrer');
        noteOpenedInNewTab(highlighted);
      } else {
        pick(highlighted);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      if (clearOnEscape) {
        onValueChange('');
      }
      setOpen(false);
    }
  }

  const emptyQueryContent =
    open && value.trim().length === 0
      ? renderEmptyQuery?.(() => setOpen(false))
      : null;

  return (
    <div ref={wrapperRef} className={`relative ${className ?? ''}`}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        className={inputClassName}
        style={inputStyle}
        autoComplete="off"
        spellCheck={false}
        autoFocus={autoFocus}
      />
      {renderTrailing?.(loading)}

      {emptyQueryContent}

      {open && value.trim().length > 0 && results.length > 0 && (
        <ul className="absolute inset-x-0 z-50 mt-1 max-h-80 overflow-y-auto border border-ink bg-card text-ink">
          {results.map((user, idx) => (
            <UserResultRow
              key={user.id}
              oid={user.id}
              displayName={user.displayName}
              userPrincipalName={user.userPrincipalName}
              href={getItemHref?.(user)}
              active={idx === cursor}
              onSelect={() => pick(user)}
              onOpenInNewTab={() => noteOpenedInNewTab(user)}
              onMouseEnter={() => setCursor(idx)}
            />
          ))}
        </ul>
      )}

      {open &&
        !loading &&
        results.length === 0 &&
        debouncedQuery.length >= 2 &&
        value.trim().length >= 2 && (
          <div className="absolute inset-x-0 z-50 mt-1 border border-ink bg-card px-3 py-2 text-xs text-muted">
            {t('topSearch.empty', 'Aucun résultat')}
          </div>
        )}
    </div>
  );
}
